import { inject } from '@angular/core';
import type { ICliente } from 'modelos';

import { SesionStore } from '@core/sesion/sesion.store';
import type { Punto } from '@core/geo/trazado';
import type { EncuadreInicialMapa, LimitesMapa } from '@core/ui/encuadre-inicial-mapa';

/**
 * Los mapas sin datos arrancan sobre el polígono del cliente elegido (el del
 * selector, o el propio si no eligió otro). Se cablea en `app.config.ts` como
 * `ENCUADRE_INICIAL_MAPA`.
 */
export function encuadreDelCliente(): EncuadreInicialMapa {
  const sesion = inject(SesionStore);
  return () =>
    limitesDe(contornosDelCliente((sesion.cliente() ?? sesion.clientePropio())?.poligono));
}

/**
 * Los anillos exteriores de `cliente.poligono`, en [lng, lat].
 *
 * Tolera lo que hay en la base:
 * - En casi todos los clientes el MultiPolygon viene con `coordinates: []`.
 * - Algunos (en producción, al 07/10/2026: Lanús, CITYMESH PRUEBAS,
 *   store-verification) lo tienen en **metros** (Web Mercator, EPSG:3857) y no
 *   en grados. Se convierten: pasados tal cual, Mapbox rechaza la latitud y el
 *   mapa no se crea.
 */
export function contornosDelCliente(poligono: ICliente['poligono']): Punto[][] {
  return (poligono?.coordinates ?? [])
    .map((anillos) =>
      (anillos?.[0] ?? [])
        .filter((p) => Number.isFinite(p?.[0]) && Number.isFinite(p?.[1]))
        .map(([x, y]) => aGrados(x, y)),
    )
    .filter((anillo): anillo is Punto[] => anillo.every((p) => p !== null) && anillo.length >= 3);
}

const RADIO_TIERRA = 6_378_137;

/** Un punto en grados tal cual; uno en metros de Web Mercator, convertido. */
function aGrados(x: number, y: number): Punto | null {
  if (Math.abs(x) <= 180 && Math.abs(y) <= 90) return [x, y];
  const lng = (x / RADIO_TIERRA) * (180 / Math.PI);
  const lat = (2 * Math.atan(Math.exp(y / RADIO_TIERRA)) - Math.PI / 2) * (180 / Math.PI);
  return Math.abs(lng) <= 180 && Math.abs(lat) <= 90 ? [lng, lat] : null;
}

function limitesDe(contornos: readonly (readonly Punto[])[]): LimitesMapa | null {
  let [oeste, sur, este, norte] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const [x, y] of contornos.flat()) {
    oeste = Math.min(oeste, x);
    este = Math.max(este, x);
    sur = Math.min(sur, y);
    norte = Math.max(norte, y);
  }
  if (!Number.isFinite(oeste)) return null;
  return [
    [oeste, sur],
    [este, norte],
  ];
}
